import { Color, SRGBColorSpace } from 'three';

/** The only colours in the world (spec palette). Mixes of two palette colours are used for bands and tints. */
export const HEX = {
  red: '#B8352B',
  cream: '#F3E6C8',
  ochre: '#EDC27A',
  grass: '#D8AE5E',
  terracotta: '#C8784A',
  olive: '#8E9A58',
  cypress: '#2F4A2E',
  asphalt: '#8C7B6B',
  brown: '#3B2A1E',
} as const;

export type PaletteName = keyof typeof HEX;

export const PAL: Record<PaletteName, Color> = Object.fromEntries(
  Object.entries(HEX).map(([k, v]) => [k, new Color(v)]),
) as Record<PaletteName, Color>;

function hexRgb(h: string): [number, number, number] {
  const n = parseInt(h.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Blend of two palette colours (in sRGB, as a painter would mix them). */
export function mix(a: PaletteName, b: PaletteName, t: number): Color {
  const A = hexRgb(HEX[a]);
  const B = hexRgb(HEX[b]);
  return new Color().setRGB(A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t, SRGBColorSpace);
}

/** CSS string of a palette mix (for canvases and the UI). */
export function mixCss(a: PaletteName, b: PaletteName, t: number): string {
  const A = hexRgb(HEX[a]);
  const B = hexRgb(HEX[b]);
  const c = [0, 1, 2].map((k) => Math.round((A[k] + (B[k] - A[k]) * t) * 255));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}
